import React, { useState } from 'react';
import { Button } from '../design';

const DEVICE_TYPES = [
  { value: 'other', label: 'Other' },
  { value: 'router', label: 'Router' },
  { value: 'switch', label: 'Switch' },
  { value: 'firewall', label: 'Firewall' },
  { value: 'access_point', label: 'Access Point' },
];

const VENDORS = [
  { value: '', label: 'Auto-detect' },
  { value: 'Netgear', label: 'Netgear' },
  { value: 'OPNSense', label: 'OPNSense' },
  { value: 'Cisco', label: 'Cisco' },
  { value: 'Juniper', label: 'Juniper' },
  { value: 'MikroTik', label: 'MikroTik' },
  { value: 'Ubiquiti', label: 'Ubiquiti' },
  { value: 'Fortinet', label: 'Fortinet' },
  { value: 'HP', label: 'HP' },
  { value: 'Dell', label: 'Dell' },
];

const SNMP_VERSIONS = [
  { value: '2c', label: 'SNMPv2c' },
  { value: '3', label: 'SNMPv3' },
];

const CHECK_METHODS = [
  { value: 'snmp', label: 'SNMP' },
  { value: 'ping', label: 'Ping Only' },
  { value: 'snmp_then_ping', label: 'SNMP then Ping' },
];

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };

export default function DeviceForm({ initialData, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    name: initialData?.name || '',
    ip_address: initialData?.ip_address || '',
    device_type: initialData?.device_type || 'other',
    vendor: initialData?.vendor || '',
    model: initialData?.model || '',
    snmp_version: initialData?.snmp_version || '2c',
    snmp_community: initialData?.snmp_community || 'public',
    snmp_port: initialData?.snmp_port || 161,
    snmp_v3_config: initialData?.snmp_v3_config || null,
    check_method: initialData?.check_method || 'snmp',
    location: initialData?.location || '',
    site: initialData?.site || '',
    environment: initialData?.environment || '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.ip_address.trim()) e.ip_address = 'IP address is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(form);
  };

  const errStyle = (k) => errors[k] ? { borderColor: 'var(--danger)' } : undefined;

  return (
    <form onSubmit={handleSubmit}>
      <div style={grid}>
        <div className="field">
          <label>Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            style={errStyle('name')}
            placeholder="e.g. Core Switch"
          />
          {errors.name && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.name}</p>}
        </div>
        <div className="field">
          <label>IP Address *</label>
          <input
            type="text"
            value={form.ip_address}
            onChange={(e) => handleChange('ip_address', e.target.value)}
            style={errStyle('ip_address')}
            placeholder="10.10.0.1"
          />
          {errors.ip_address && <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{errors.ip_address}</p>}
        </div>
      </div>

      <div style={grid}>
        <div className="field">
          <label>Type</label>
          <select
            value={form.device_type}
            onChange={(e) => handleChange('device_type', e.target.value)}
          >
            {DEVICE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Vendor</label>
          <select
            value={form.vendor}
            onChange={(e) => handleChange('vendor', e.target.value)}
          >
            {VENDORS.map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Model</label>
          <input
            type="text"
            value={form.model}
            onChange={(e) => handleChange('model', e.target.value)}
            placeholder="e.g. GS308E"
          />
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 16 }}>
        <h3 className="muted" style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>SNMP Configuration</h3>
        <div style={grid}>
          <div className="field">
            <label>Version</label>
            <select
              value={form.snmp_version}
              onChange={(e) => handleChange('snmp_version', e.target.value)}
            >
              {SNMP_VERSIONS.map((v) => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Community</label>
            <input
              type="text"
              value={form.snmp_community}
              onChange={(e) => handleChange('snmp_community', e.target.value)}
            />
          </div>
          <div className="field">
            <label>Port</label>
            <input
              type="number"
              value={form.snmp_port}
              onChange={(e) => handleChange('snmp_port', parseInt(e.target.value) || 161)}
            />
          </div>
        </div>
        <div className="field">
          <label>Check Method</label>
          <select
            value={form.check_method}
            onChange={(e) => handleChange('check_method', e.target.value)}
          >
            {CHECK_METHODS.map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>

        {form.snmp_version === '3' && (
          <div style={{ borderTop: '1px solid var(--border-strong)', paddingTop: 16, marginTop: 16 }}>
            <h4 className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 12 }}>SNMPv3 Settings</h4>
            <div style={grid}>
              <div className="field">
                <label>Security Name</label>
                <input
                  type="text"
                  value={form.snmp_v3_config?.security_name || ''}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, security_name: e.target.value })}
                  placeholder="e.g. snmpuser"
                />
              </div>
              <div className="field">
                <label>Auth Protocol</label>
                <select
                  value={form.snmp_v3_config?.auth_protocol || 'SHA'}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, auth_protocol: e.target.value })}
                >
                  <option value="SHA">SHA</option>
                  <option value="MD5">MD5</option>
                </select>
              </div>
              <div className="field">
                <label>Auth Password</label>
                <input
                  type="password"
                  value={form.snmp_v3_config?.auth_password || ''}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, auth_password: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Privacy Protocol</label>
                <select
                  value={form.snmp_v3_config?.priv_protocol || 'AES'}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, priv_protocol: e.target.value })}
                >
                  <option value="AES">AES</option>
                  <option value="DES">DES</option>
                </select>
              </div>
              <div className="field">
                <label>Privacy Password</label>
                <input
                  type="password"
                  value={form.snmp_v3_config?.priv_password || ''}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, priv_password: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Security Level</label>
                <select
                  value={form.snmp_v3_config?.security_level || 'authPriv'}
                  onChange={(e) => handleChange('snmp_v3_config', { ...form.snmp_v3_config, security_level: e.target.value })}
                >
                  <option value="noAuthNoPriv">noAuthNoPriv</option>
                  <option value="authNoPriv">authNoPriv</option>
                  <option value="authPriv">authPriv</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={grid}>
        <div className="field">
          <label>Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => handleChange('location', e.target.value)}
          />
        </div>
        <div className="field">
          <label>Site</label>
          <input
            type="text"
            value={form.site}
            onChange={(e) => handleChange('site', e.target.value)}
          />
        </div>
        <div className="field">
          <label>Environment</label>
          <select
            value={form.environment}
            onChange={(e) => handleChange('environment', e.target.value)}
          >
            <option value="">--</option>
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="development">Development</option>
            <option value="lab">Lab</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2" style={{ justifyContent: 'flex-end', paddingTop: 8 }}>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit" variant="primary" disabled={loading}>{loading ? 'Saving...' : 'Save Device'}</Button>
      </div>
    </form>
  );
}
